import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "utilities/auth";
import { createTypeOptionsFor } from "utilities/companyCreate";
import { Plus, ChevronDown } from "lucide-react";

/**
 * The single Create control on /companies (SOUND-296).
 *
 * A split button: the face is "Create Client" — by far the most common action,
 * so it stays one click — and the caret opens the rest of the menu.
 *
 * WHO SEES WHAT is decided entirely by `createTypeOptionsFor(auth.user)`:
 * internal gets Client · Parent Company · Partner, everyone else gets Client
 * only. That helper is the only enforcement that a partner cannot create a
 * parent or a partner — a "parent" create is a client create with no
 * application, so the API cannot tell them apart. It is unit-tested in
 * `utilities/companyCreate.test.js`; do not re-derive the rule here.
 *
 * With one option there is no caret at all, so a partner sees exactly the
 * plain "Create Client" button they see today.
 *
 * Menu chrome copied from `StatusControl` in `pages/tickets/Ticket.jsx`.
 */
export default function CreateCompanyMenu({ onSelect }) {
  const auth = useAuth();
  const options = createTypeOptionsFor(auth?.user);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (options.length === 0) return null;

  const primary = options[0];

  // One option (partner users) — a plain button, no menu affordance.
  if (options.length === 1) {
    return (
      <button className="btn btn-primary" onClick={() => onSelect(primary.type)}>
        <Plus className="h-4 w-4" />
        {primary.modalTitle}
      </button>
    );
  }

  return (
    <div className="relative inline-flex" ref={ref}>
      <button
        className="btn btn-primary rounded-r-none"
        onClick={() => onSelect(primary.type)}
      >
        <Plus className="h-4 w-4" />
        {primary.modalTitle}
      </button>
      <button
        className="btn btn-primary rounded-l-none border-l border-brand-foreground/25 px-2"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="More create options"
        onClick={() => setOpen((o) => !o)}
      >
        <ChevronDown
          className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-[calc(100%+6px)] z-30 min-w-[260px] animate-scale-in rounded-lg border border-border bg-card p-1.5 shadow-lg"
        >
          <div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            Create
          </div>
          {options.map((opt) => (
            <button
              key={opt.type}
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onSelect(opt.type);
              }}
              className="flex w-full flex-col items-start gap-0.5 rounded-md px-2 py-1.5 text-left hover:bg-muted"
            >
              <span className="text-sm font-medium text-foreground">{opt.label}</span>
              <span className="text-xs text-muted-foreground">
                {opt.menuDescription}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
